import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { queryCppJuniorProspectsAPI } from '../../../../actions/college-corner.actions';
import Table from '../../../../components/Table';
import { parseQueryParams, setQueryParams } from '../../../../utils/collegeCorner';
import CollegeCornerFilters from '../../filters/CollegeCornerFilters';
import { initialState, SORT_ORDER } from '../helpers';
import { getCppColumns, getCppInitialFilters } from './cppHelpers';
import styles from './CppJuniorProspects.scss';

const NO_RESULTS_TEXT = 'There are no results that meet this criteria.';

function CppJuniorProspects({ history, location, collegeCoachTeamGenders }) {
    const dispatch = useDispatch();
    const [data, setData] = useState(initialState);
    const [filters, setFilters] = useState(null);
    const [tempFilters, setTempFilters] = useState({});

    const columns = useMemo(() => getCppColumns({ collegeCoachTeamGenders }), [collegeCoachTeamGenders]);
    const initialFilters = useMemo(() => getCppInitialFilters(collegeCoachTeamGenders), [collegeCoachTeamGenders]);

    const callJuniorProspectsAPI = (params) => dispatch(queryCppJuniorProspectsAPI(params));

    useEffect(() => {
        const parsed = parseQueryParams(location.search);
        const sortableKeys = columns.filter((col) => col.sortable !== false).map((col) => col.key);
        if (parsed.sort && !sortableKeys.includes(parsed.sort)) {
            delete parsed.sort;
            delete parsed.order;
        }
        setFilters({ ...initialFilters, ...parsed });
    }, [initialFilters]);

    useEffect(() => {
        if (filters) {
            const query = setQueryParams(filters);
            history.replace(`${location.pathname}?${query}`);
        }
    }, [filters]);

    const onAPILoad = async (apiData, reset = false) => {
        if (reset) {
            setData(initialState);
        }
        const response = await callJuniorProspectsAPI(apiData);
        const { rows, count } = response || {};
        if (rows) {
            setData(() => ({
                rows,
                count,
                isLoad: false,
            }));
        }
    };

    const onhandleSort = (sortValue) => {
        if (sortValue && sortValue === filters.sort) {
            setFilters((prevState) => ({
                ...prevState,
                order: filters.order === SORT_ORDER.ASC ? SORT_ORDER.DESC : SORT_ORDER.ASC,
                page: 1,
            }));
        } else {
            setFilters((prevState) => ({
                ...prevState,
                sort: sortValue,
                order: SORT_ORDER.DESC,
                page: 1,
            }));
        }
    };

    const onLoadMore = () => {
        setFilters((prevState) => ({
            ...prevState,
            page: prevState.page + 1,
        }));
    };

    const isPropsValid = (props) => {
        return props
            ? Object.keys(props).filter((v) => Object.keys(filters).indexOf(v) > -1)
                .length > 0
            : false;
    };

    const onhandleUpdateFilters = (updatedFilters) => {
        const updatedProps = isPropsValid(updatedFilters) ? updatedFilters : {};
        setTempFilters((prevState) => ({
            ...prevState,
            ...updatedProps,
        }));
    };

    const onhandleFilterSubmit = (props = {}) => {
        const updatedProps = isPropsValid(props) ? props : {};
        setFilters((prevState) => ({
            ...prevState,
            ...tempFilters,
            ...updatedProps,
            page: 1,
        }));
        setTempFilters({});
    };

    const onhandleResetFilters = () => {
        setTempFilters({});
        setFilters({ ...initialFilters });
    };

    return (
        <div className={`${styles.CppJuniorProspects}`}>
            <CollegeCornerFilters
                tempFilters={tempFilters}
                defaultFilters={filters}
                onhandleUpdateFilters={onhandleUpdateFilters}
                onhandleFilterSubmit={onhandleFilterSubmit}
                onhandleResetFilters={onhandleResetFilters}
                showGradYearFilter={true}
                filterOptions={['gender', 'utr', 'highSchool', 'recruitingStatus', 'location', 'nationality']}
                sortingOptions={columns
                    .filter((v) => v.sortable !== false)
                    .map((v) => ({ label: v.title, value: v.key }))}
            />
            <Table
                sortRuleChanger={onhandleSort}
                columns={columns}
                data={data}
                filters={filters}
                asyncCall={onAPILoad}
                onhandleClickLoadMore={onLoadMore}
                customNoRowsText={NO_RESULTS_TEXT}
                mergeData
            />
        </div>
    );
}

CppJuniorProspects.propTypes = {
    history: PropTypes.instanceOf(Object).isRequired,
    location: PropTypes.instanceOf(Object).isRequired,
    collegeCoachTeamGenders: PropTypes.arrayOf(PropTypes.string),
};

CppJuniorProspects.defaultProps = {
    collegeCoachTeamGenders: [],
};

export default CppJuniorProspects;
